import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {
  DEFAULT_STEM,
  DEFAULT_WINDOW,
  HairpinParameter,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import FormInput from 'ui-library/bs/FormInput';
import FieldError from 'ui-library/FieldError';
import HelpIcon from 'ui-library/icons/HelpIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import {InputChangeEvent} from 'util/Types';

interface CodonOptimizationHairpinParametersProps {
  bases?: string;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter;
  hairpinWindow: HairpinParameter;
  onUpdateAvoidHairpins: (checked: boolean) => void;
  onUpdateHairpinStem: (stem: string) => void;
  onUpdateHairpinWindow: (window: string) => void;
}

interface HairpinInfoModalProps {
  onRequestClose: () => void;
}

function HairpinInfoModal(props: HairpinInfoModalProps): JSX.Element {
  return (
    <Modal
      title="Hairpins Information"
      onRequestClose={props.onRequestClose}
      buttons={[{text: 'Dismiss', cancel: true}]}
    >
      <p>
        Hairpins can occur when a single strand of RNA folds and binds to itself. RNA hairpins can impact
        protein expression and synthesis. This checkbox applies the{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://edinburgh-genome-foundry.github.io/DnaChisel/_modules/dnachisel/builtin_specifications/AvoidHairpins.html#AvoidHairpins"
        >
          AvoidHairpins{' '}
        </a>
        specification in{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="https://edinburgh-genome-foundry.github.io/DnaChisel/"
        >
          DNAChisel{' '}
        </a>
        to your codon optimization. By default, we search for hairpins with a minimum binding size of{' '}
        {DEFAULT_STEM} bases, within a window of {DEFAULT_WINDOW} bases.
      </p>
    </Modal>
  );
}

function showHairpinInfoModal(): void {
  legacyRunModal(HairpinInfoModal, {});
}

function CodonOptimizationHairpinParameters(props: CodonOptimizationHairpinParametersProps): JSX.Element {
  return (
    <div>
      <div className={css(styles.inputLabel)}>
        <CheckboxInput
          checked={props.avoidHairpins}
          id="avoidHairpins"
          label={<FieldLabel label="Hairpin parameters" />}
          disabled={false}
          onChange={(e: InputChangeEvent) => {
            props.onUpdateAvoidHairpins(e.target.checked);
          }}
          dataTestAttrs={{
            component: 'CodonOptimizationHairpinParameters',
            element: 'checkbox',
          }}
        />
        <HelpIcon onClick={showHairpinInfoModal} />
      </div>
      <div className={css(styles.subsection)}>
        <p className={css(styles.explanationText)}>
          Hairpins can occur when a single strand of RNA folds and binds to itself. RNA hairpins can impact
          protein expression and synthesis.
        </p>
        <div className={css(styles.paramInputs)}>
          <div>
            <p className={css(styles.paramInputLabel)}>Stem size</p>
            <FormInput
              error={props.hairpinStem.error}
              type="number"
              className={`hairpinStem ${css(styles.numberInput)}`}
              placeholder="Stem Size"
              value={props.avoidHairpins && props.hairpinStem.value !== 0 ? props.hairpinStem.value : ''}
              onChange={(e: InputChangeEvent) => {
                props.onUpdateHairpinStem(e.target.value);
              }}
              focusOnMount={false}
              selectAllOnClick={true}
              disabled={!props.avoidHairpins}
              dataTestAttrs={{
                component: 'CodonOptimizationHairpinParameters',
                element: 'stemSizeInput',
              }}
            />
          </div>
          <div>
            <p className={css(styles.paramInputLabel)}>Window</p>
            <FormInput
              error={props.hairpinWindow.error}
              type="number"
              className={`hairpinWindow ${css(styles.numberInput)}`}
              placeholder="Window"
              value={props.avoidHairpins && props.hairpinWindow.value !== 0 ? props.hairpinWindow.value : ''}
              onChange={(e: InputChangeEvent) => {
                props.onUpdateHairpinWindow(e.target.value);
              }}
              focusOnMount={false}
              selectAllOnClick={true}
              disabled={!props.avoidHairpins}
              dataTestAttrs={{
                component: 'CodonOptimizationHairpinParameters',
                element: 'windowInput',
              }}
            />
          </div>
        </div>
        <FieldError message={props.hairpinStem.error} />
        <FieldError message={props.hairpinWindow.error} />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  inputLabel: {
    display: 'flex',
    gap: 4,
  },
  paramInputs: {
    display: 'flex',
    gap: 8,
  },
  paramInputLabel: {
    marginBottom: 4,
  },
  numberInput: {
    width: 90,
  },
  explanationText: {
    color: Color.GRAY_700,
  },
  subsection: {
    marginLeft: 20,
  },
});

export default CodonOptimizationHairpinParameters;
